export enum PdfProperties {
  POINT_TO_PIXEL_RATIO = 1.333,
  BACKGROUND_COLOR = "#FFFFFF",
  DRAEGERBLUE = "#002766",
  ODX_RED = "#BC0000",
  ODX_YELLOW = "#F57A00",
  ODX_GREY = "#798EA0",
  FOOTER_COLOR = "#D5E2F6",
  HEADLINE_FONT_SIZE = 64 * POINT_TO_PIXEL_RATIO,
  HEADLINE_Y = 104,
  BORDER_WIDTH = 3,
  BORDER_COLOR = "#C7D3E0",
  TABLE_OF_CONTENTS_FONT_SIZE = 25 * PdfProperties.POINT_TO_PIXEL_RATIO,
  TABLE_OF_CONTENTS_FONT_SIZE_LARGE = 36 * PdfProperties.POINT_TO_PIXEL_RATIO,
}

export enum ContainerProperties {
  HEIGHT = 1975,
  WIDTH = 1500,
  LEFT_X = 145,
  RIGHT_X = 1886,
  Y = 255,
  PADDING_Y = 16,
}

export enum FontNames {
  DRAEGER_PANGEA = "DraegerPangea-Medium",
  DRAEGER_PANGEA_TEXT = "DraegerPangeaText-Regular",
  DRAEGER_PANGEA_TEXT_BOLD = "DraegerPangeaText-Bold",
}

export enum TextProperties {
  BASELINE_TOP = "top",
}

export enum ImagePaths {
  LEGEND_DE = "assets/legend_de.png",
  LEGEND_EN = "assets/legend_en.png",
  LEGEND_ES = "assets/legend_es.png",
  LEGEND_FR = "assets/legend_fr.png",
  LEGEND_PT = "assets/legend_pt.png",
  LEGEND_MONITORING_EN = "assets/legend_monitoring_en.png",
  DRAEGERLOGO = "assets/draeger-logo.png",
  NAVINTALOGO = "assets/navinta-logo.png",
  WATERMARK = "assets/watermark.png",
  GASWARNINGCENTER = "assets/icons-pdf/gaswarnzentrale_pdf.png",
  GASWARNINGCENTER_PLACEHOLDER = "assets/icons-pdf/gaswarnzentrale_platzhalter_pdf.png",
  TRANSMITTER = "assets/icons-pdf/transmitter_pdf.png",
  TRANSMITTER_PLACEHOLDER = "assets/icons-pdf/transmitter_platzhalter_pdf.png",
  FLAME_DETECTOR = "assets/icons-pdf/brandmelder_pdf.png",
  FLAME_DETECTOR_PLACEHOLDER = "assets/icons-pdf/brandmelder_platzhalter_pdf.png",
  ALARMDEVICE = "assets/icons-pdf/alarmmittel_pdf.png",
  ALARMDEVICE_PLACEHOLDER = "assets/icons-pdf/alarmmittel_platzhalter_pdf.png",
  SIGNALELEMENT = "assets/icons-pdf/leuchttransparent_pdf.png",
  SIGNALELEMENT_PLACEHOLDER = "assets/icons-pdf/leuchttransparent_platzhalter_pdf.png",
  PLASTICSIGN = "assets/icons-pdf/kunststoffschild_pdf.png",
  PLASTICSIGN_PLACEHOLDER = "assets/icons-pdf/kunststoffschild_platzhalter_pdf.png",
  ZONE = "assets/icons-pdf/zonen_pdf.png",
  PIPELINE = "assets/icons-pdf/rohrleitungen_pdf.png",
  CABLE_ROUTE = "assets/icons-pdf/kabeltrasse_pdf.png",
  PROTECTIVE_TUBE = "assets/icons-pdf/rohrleitungen_pdf.png",
  WIRE = "assets/icons-pdf/leitung_pdf.png",

  MEASUREMENT_LINE = "assets/icons-pdf/bemassungen_pdf.png",
  NOTE = "assets/icons-pdf/notizen_pdf.png",
  AIR_PATH = "assets/icons-pdf/zu_abluftweg_pdf.png",
  IMAGE = "assets/icons-pdf/picture_pdf.png",
  WARNING_SIGN = "assets/icons-pdf/warning_colored_pdf.png",
  SWITCH_CHECKED = "assets/icons-pdf/odx_switch_checked_pdf.png",
  SWITCH_UNCHECKED = "assets/icons-pdf/odx_switch_unchecked_pdf.png",
}

export enum ImageTypes {
  PNG = "png",
}

export class Headlines {
  // Monitoring
  public readonly ACCEPTANCE = $localize`:@@monitoring.pdfExport.headline.acceptance:Acceptance by the parties`;
  public readonly CENTRAL_STATIONS = $localize`:@@monitoring.pdfExport.headline.centralStations:Central Stations`;
  public readonly PATIENT_MONITORS = $localize`:@@monitoring.pdfExport.headline.patientMonitors:Patienten Monitore`;
  public readonly TELEMETRY = $localize`:@@monitoring.pdfExport.headline.telemetry:Telemetry`;
  public readonly KVMS = $localize`:@@monitoring.pdfExport.headline.kvms:KVMs`;
  public readonly DISPLAYS = $localize`:@@monitoring.pdfExport.headline.displays:Displays`;
  public readonly UTILITY = $localize`:@@monitoring.pdfExport.headline.utility:Utility`;

  // FGDS
  public readonly ALARMDEVICES = $localize`:@@pdfExport.headline.alarmDevices:Alarmmittel`;
  public readonly ADDITIONAL_PRODUCTS = $localize`:@@productList.additionalProducts:Zusätzliche Produkte`;
  public readonly ATTACHMENT_INSTRUCTIONS = $localize`:@@productList.attachmentInstructions:Gebrauchsanweisungen für Zubehör`;
  public readonly CABLE_ROUTES = $localize`:@@pdfExport.headline.cableRoutes:Kabeltrassen`;
  public readonly FLOORPLAN = $localize`:@@pdfExport.headline.floorplan:Grundrissplan`;
  public readonly FLOORPLAN_INFORMATION = $localize`:@@pdfExport.headline.floorplanInformation:Grundrissplan Informationen`;
  public readonly GASWARNINGCENTERS = $localize`:@@pdfExport.headline.gasWarningCenters:Gaswarnzentralen`;
  public readonly LEGEND = $localize`:@@pdfExport.headline.legend:Legende`;
  public readonly PIPELINES = $localize`:@@pdfExport.headline.pipelines:Rohrleitungen`;
  public readonly PLASTICSIGNS = $localize`:@@pdfExport.headline.plasticSigns:Kunststoffschilder`;
  public readonly PRODUCT_LIST = $localize`:@@pdfExport.headline.productList:Produktliste`;
  public readonly PRODUCT_LIST_OVERALL = $localize`:@@pdfExport.headline.productListOverall:Gesamtproduktliste – alle Grundrisse`;
  public readonly PRODUCT_NOTES = $localize`:@@pdfExport.headline.productNotes:Produktnotizen`;
  public readonly PROTECTIVE_TUBES = $localize`:@@pdfExport.headline.protectiveTubes:Schutzrohre`;
  public readonly SIGNALELEMENTS = $localize`:@@pdfExport.headline.signalElements:Leuchttransparente`;
  public readonly TRANSMITTERS = $localize`:@@pdfExport.headline.transmitters:Transmitter`;
  public readonly FLAME_DETECTORS = $localize`:@@pdfExport.headline.flameDetectors:Brandmelder`;
  public readonly WIRES = $localize`:@@pdfExport.headline.wires:Leitungen`;
  public readonly ZONES = $localize`:@@pdfExport.headline.zones:Zonen`;

  public readonly MEASUREMENT_LINES = $localize`:@@pdfExport.headline.measurements:Bemaßungen`;
  public readonly REFERENCE_MEASURE = $localize`:@@pdfExport.headline.referenceMeasure:Referenzmaß`;
  public readonly NOTES = $localize`:@@pdfExport.headline.notes:Notizen`;
  public readonly FREE_TEXTS = $localize`:@@pdfExport.headline.freeTexts:Freitexte`;
  public readonly AIR_PATHS = $localize`:@@pdfExport.headline.airPaths:Zu-/Abluftwege`;
  public readonly IMAGES = $localize`:@@pdfExport.headline.images:Fotos`;
  public readonly GASWARNINGCENTERS_PLACEHOLDERS = $localize`:@@pdfExport.headline.gasWarningCenterPlaceholders:Gaswarnzentralen (Platzhalter)`;
  public readonly TRANSMITTERS_PLACEHOLDERS = $localize`:@@pdfExport.headline.transmitterPlaceholders:Transmitter (Platzhalter)`;
  public readonly FLAME_DETECTOR_PLACEHOLDERS = $localize`:@@pdfExport.headline.flameDetectorPlaceholders:Brandmelder (Platzhalter)`;
  public readonly ALARMDEVICES_PLACEHOLDERS = $localize`:@@pdfExport.headline.alarmDevicePlaceholders:Alarmmittel (Platzhalter)`;
  public readonly SIGNALELEMENTS_PLACEHOLDERS = $localize`:@@pdfExport.headline.signalElementPlaceholders:Leuchttransparente (Platzhalter)`;
  public readonly PLASTICSIGNS_PLACEHOLDERS = $localize`:@@pdfExport.headline.plasticSignPlaceholders:Kunststoffschilder (Platzhalter)`;
  public readonly SERVICES = $localize`:@@pdfExport.headline.services:Dienstleistungen`;
  public readonly CHECKLIST = $localize`:@@pdfExport.headline.checklist:Checkliste`;
  public readonly TABLE_OF_CONTENTS = $localize`:@@pdfExport.headline.tableOfContents:Inhaltsverzeichnis`;
  public readonly PROJECT_NOTES = $localize`:@@pdfExport.project.notes:Projektnotizen`;
  public readonly PROJECT_INFORMATION = $localize`:@@projectInformation.header:Projektinformationen`;
}

// TODO don't use enum for holding static constants
// afterwards re-enable rule "@typescript-eslint/no-duplicate-enum-values" in .eslintrc.json
export enum TableProperties {
  EMPTY_TABLE_HEIGHT = 152,
  GAP_BETWEEN_TABLES = 84,
  HEADLINE_OFFSET_Y = 30,
  HEADLINE_ICON_WIDTH = 24,
  HEADLINE_ICON_HEIGHT = 24,
  WARNING_ICON_SIZE = 20,
  GAP_HEADLINE_HEADERS = 82,
  PADDING_X = 48,
  PADDING_12 = 12,
  PADDING_26 = 26,
  PADDING_32 = 32,
  PADDING_48 = 48,
  PADDING_64 = 64,
  ROW_PADDING_TOP = 14,
  ROW_PADDING_BOTTOM = 8,
  ROW_PADDING_Y = ROW_PADDING_BOTTOM + ROW_PADDING_TOP,
  ROW_PADDING_X = 16,
  ROW_SEPARATOR_LENGTH = 1404,
  SEPARATOR_COLOR = "#DDE5EE",
  SEPARATOR_COLOR_STRONG = "#5D6A74",
  SEPARATOR_WIDTH = 1,
  HEADER_FONT_SIZE = 25 * PdfProperties.POINT_TO_PIXEL_RATIO,
  HEADER_HEIGHT = 96,
  TEXT_FONT_SIZE = 20 * PdfProperties.POINT_TO_PIXEL_RATIO,
  TEXT_HEIGHT = 26.65,
}

export enum ProductListProperties {
  ROOT_SECTION_FONT_SIZE = 28 * PdfProperties.POINT_TO_PIXEL_RATIO,
  SUMMARY_FONT_SIZE = 25 * PdfProperties.POINT_TO_PIXEL_RATIO,
  FULL_SUMMARY_HEIGHT = 192,
  SUMMARY_HEIGHT = 96,
  FOOTER_HEIGHT = 126,
}
